import { matchPath, useLocation } from 'react-router-dom';

import { Skeleton } from '@flintmint/web-shared/components/skeleton';

import { appRoutes } from '@/constants/routes';
import { AccessListPageSkeleton } from '@/features/access-list/access-list-page';
import { AiUsagePricingPageSkeleton } from '@/features/ai-usage-pricing/ai-usage-pricing-skeleton';
import { FlowDashboardSkeleton } from '@/features/flow-dashboard/flow-dashboard';
import { MedicalModuleDashboardSkeleton } from '@/features/medical-module-dashboard/medical-module-dashboard';
import { SettingsPageSkeleton } from '@/features/settings/settings-page';
import { UserDetailsPageSkeleton } from '@/features/users/user-details-skeleton';
import { UsersPageSkeleton } from '@/features/users/users-page';

import styles from './route-skeleton.module.scss';

/** Stands in for the current route's page while auth settles or its chunk loads, as that page looks while its data loads. */
export const RouteSkeleton = () => {
  const { pathname } = useLocation();

  return <div data-testid="page-skeleton">{renderPageSkeleton(pathname)}</div>;
};

const renderPageSkeleton = (pathname: string) => {
  const userDetailsMatch = matchPath(appRoutes.userDetails, pathname);
  if (userDetailsMatch) return <UserDetailsPageSkeleton userId={decodeURIComponent(userDetailsMatch.params.userId ?? '')} />;

  switch (pathname) {
    case appRoutes.home:
    case appRoutes.flowModules:
      return <MedicalModuleDashboardSkeleton />;
    case appRoutes.flows:
      return <FlowDashboardSkeleton />;
    case appRoutes.users:
      return <UsersPageSkeleton />;
    case appRoutes.accessList:
      return <AccessListPageSkeleton />;
    case appRoutes.aiUsagePricing:
    case appRoutes.legacyTokenUsage:
      return <AiUsagePricingPageSkeleton />;
    case appRoutes.settings:
      return <SettingsPageSkeleton />;
    default:
      return <GenericPageSkeleton />;
  }
};

const GENERIC_ROW_COUNT = 4;

const GenericPageSkeleton = () => (
  <div className={styles.generic}>
    <Skeleton width={220} height={28} />
    {Array.from({ length: GENERIC_ROW_COUNT }, (_, index) => (
      <Skeleton key={index} width="100%" height={24} />
    ))}
  </div>
);
